import { useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLabel } from '@platform/hooks/useLabel';
import { routes } from '@platform/core/lib/routes';
import { modulesLink, systemItem, systemModules, useOpenInWorkspace } from '@platform/navigation';
import type { INavGroup } from '@platform/navigation';
import { appRoutes } from './appRoutes';
import { chipTable } from './chipTable';
import { FloatingMenu } from './FloatingMenu';
import { GroupPill } from './GroupPill';
import { groupRows } from './groupRows';
import type { IGroupNavProps } from './IGroupNavProps';
import { keepActiveVisible } from './keepActiveVisible';
import { menuAnchor } from './menuAnchor';
import { useShellNav } from './useShellNav';

/**
 * Смуга груп модулів режиму навігації (UIUX top-bar «Navigation modes», прототип v4): пігулки груп у порядку режиму (лише групи, що мають
 * модулі в просторі; власні модулі — у групі своєї категорії), перша — «Головна»
 * (будинок); активна група — біла з «· модуль». Смуга прокручується горизонтально з розмиттям правого краю, активна пігулка завжди видима.
 * Пігулка відкриває плаваюче меню групи (прототип v4): шапка-посилання на огляд напряму, плитки модулів із лічильниками, підгрупи, якщо в
 * режимі вони не мають власної пігулки; «Верховна Рада» поза парламентським простором — орієнтир без лічильника.
 */
export function GroupNav({ ws, open, onToggle, onClose }: IGroupNavProps) {
  const { t } = useTranslation();
  const label = useLabel();
  const navigate = useNavigate();
  const openIn = useOpenInWorkspace();
  const nav = useShellNav(ws);
  const strip = useRef<HTMLElement>(null);
  const trigger = useRef<HTMLElement | null>(null);
  const [anchor, setAnchor] = useState({ x: 0, y: 0 });
  // після кожного оновлення (як прототип): підпис активної пігулки може з'явитися пізніше — після завантаження каталогу
  useLayoutEffect(() => keepActiveVisible(strip.current));
  const moduleName = (key?: string) => label(nav.catalog?.modules.find((m) => m.key === key)?.name) || key || '';
  const onSystem = (key: string) => navigate(key === 'administration' ? appRoutes.admin() : key === 'customization' ? appRoutes.customize() : routes.home());
  const parliamentWs = nav.me?.workspaces.find((w) => w.type === 'parliament');
  const rowsOf = (group: INavGroup) => groupRows({
    group, catalog: nav.catalog, module: nav.module, recordType: nav.recordType, onHome: nav.onHome, label, onSystem,
    omitSubgroups: group.subgroups.map((s) => s.key).filter((k) => nav.groups.some((g) => g.key === k)),
    parliament: nav.parliament, onLandmark: parliamentWs ? () => openIn(parliamentWs.id, routes.module('mandate')) : undefined, system: nav.system,
  });
  // групи без модулів простору не показуються (прототип: cats…filter(([,ms]) => ms.length)); доки каталог вантажиться — усі групи режиму
  const groups = nav.catalog ? nav.groups.filter((g) => g.home || rowsOf(g).some((r) => r.key.startsWith('m:'))) : nav.groups;
  const openGroup = groups.find((g) => open === `group:${g.key}`);
  const rows = openGroup ? rowsOf(openGroup) : [];
  // Підпис активної пігулки: дочірня таблиця, що є модулем простору (chipTable), інакше модуль; огляд напряму; системний модуль
  // (« · Адміністрування», « · Налаштування»); інакше — Головна
  const systemName = nav.system ? label(systemItem(nav.system === 'admin' ? 'administration' : 'customization')?.label) : '';
  const activeSub = nav.module ? chipTable(nav.catalog, nav.module, nav.recordType, label) ?? moduleName(nav.module) : nav.onArea ? t('areaPage.overview') : systemName || t('homeNav.label');
  // «N модулів» шапки: встановлені модулі групи (орієнтир «Верховна Рада» поза парламентом — не модуль простору, як tgCount прототипу);
  // у «Головній» — ще й системні модулі (Головна, Адміністрування, Налаштування), як у прототипі
  const count = rows.filter((r) => r.key.startsWith('m:')).length + (openGroup?.home ? systemModules().length : 0);
  return (
    <>
      <nav ref={strip} className="tb-nav" aria-label={t('shell.modulesNav')}>
        {groups.map((g) => {
          const active = nav.activeGroup === g.key;
          return (
            <GroupPill key={g.key} label={label(g.label)} title={label(g.title)} home={g.home} active={active} expanded={open === `group:${g.key}`}
              sub={active ? activeSub : null}
              onClick={(e) => { trigger.current = e.currentTarget; setAnchor(menuAnchor(e.currentTarget)); onToggle(`group:${g.key}`); }} />
          );
        })}
      </nav>
      {openGroup && (
        <FloatingMenu label={label(openGroup.menuTitle)} rows={rows} x={anchor.x} y={anchor.y} onClose={onClose} trigger={trigger}
          heading={{ title: label(openGroup.menuTitle), sub: t('shell.areaSub', { modules: t('shell.modulesCount', { count }) }), to: appRoutes.area(openGroup.key), hint: t('shell.openArea') }}
          footer={{ label: t('shell.showInAll'), to: modulesLink(openGroup.key) }} />
      )}
    </>
  );
}
